import React, {createRef, useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {Swiper, SwiperSlide} from "swiper/react/swiper-react";
import SwiperCore, {A11y, Controller, Pagination} from "swiper";
import {useTranslation} from 'react-i18next';
import Modal from "components/Modal";
import {AxiosError} from 'axios';
import useHistoryState from 'use-history-state';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {
	callAlertWeb,
	callNativeWeb,
	clickMoveToAppWeb,
	exceptionEventWeb,
	footerShowEventWeb,
	hideProgressWeb,
	phoneCallWeb,
	showProgressWeb,
		uploadIDCardWeb,
	reUploadIDCardWeb
} from 'common/appInterface';
import ApprovalList from './ApprovalList';
import RailplusGuide from './RailplusGuide';
import CardManage from './CardManage';
import CardContainer from "views/Main/CardContainer";
import {inquiryFunctionAppVersion} from "../../api/SelfCommonAPI";
import StringUtils from "common/stringUtils";
import NonInoutInfo from "views/CardManage/NonInoutInfo";
import {isAndroid} from "react-device-detect";
import CardApplyContainer from "views/Main/CardApplyContainer";
import CardProductBenefitHTML from "views/Main/CardProductBenefitHTML";
import {isNullOrBlank} from 'common/utils';
import CardStatusHTML from './CardStatusHTML';
import CardReuploadHTML from "views/Main/CardReuploadHTML";
import {
	CardProductCode,
	NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION,
	QUICK_CARD_REGISTER_MIN_VERSION
} from '../../constants/Card';
import { WEBVIEW_RESOURCE_VERSION } from 'common/cacheOverwrite';
import ReissueNoticeModal from 'views/Main/Modal/ReissueNotice/ReissueNoticeModal';
import moment from 'moment/moment';

export {default as BenefitsDetail} from "./BenefitsDetail";
export {default as BenefitsTab} from "./BenefitsTab";

const MainPage: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** 다국어처리 */
	const {i18n} = useTranslation();
	const screenId = "STAT01";
	const screenName = "GmoneyCard";
	
	/** 체크카드회원번호 */
	const [cardMemberNo, setCardMemberNo] = useState<string>("");
	/** 카드목록 */
	const [cardInfoList, setCardInfoList] = useState<CardInfo[]>();
	
	/** 활성화 탭목록 */
	const [tabList, setTabList] = useState<string[]>();
	
	// 메인 카드목록 생성
	const loadCardInfoList = useCallback(() => {
		showProgressWeb();
		client.get("/api/app/main", {params: location.state.query}).then((response) => {
			if (response.data.code === "00") {
				let result: CardInfo[] = response.data.response.cardList;
				
				// 초기 탭설정
				let tabs: string[] = [];
				result.forEach(() => {
					tabs.push("1");
				});
				try {
					tabs[location.state.swiperIndex] = location.state.tabIndex;
				} catch (e) {
				}
				setTabList(tabs);
				
				// 체크카드회원번호
				setCardMemberNo(response.data.response.cardMemberNo);
				
				// 보유카드목록
				console.log("cardList:::", result);
				setCardInfoList(result);
			} else {
				exceptionEventWeb(response.data.code, response.data.message, "2");
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [location]);
	
	const callMainNativeWeb = useCallback((mainYn: string) => {
		callNativeWeb(screenId, screenName, mainYn);
	}, []);
	
	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callMainNativeWeb("Y");
		// 하단바 보이기
		footerShowEventWeb("Y");
		
		console.log("QUERY PARAMS");
		console.log(location.state);
		// console.log(location.state.swiperIndex);
		
		loadCardInfoList();
		
		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	return (
		<>
			{cardInfoList &&
                <MainChild cardInfoList={cardInfoList} cardMemberNo={cardMemberNo} tabList={tabList!}
                           swiperIndex={location.state.swiperIndex} callMainNativeWeb={callMainNativeWeb}
                />
			}
		</>
	);
};

const MainChild: React.FC<MainProps> = (props) => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** 다국어처리 */
	const {t} = useTranslation();
	const outerUrl = `${process.env.REACT_APP_OUTER_HTML_PATH}/card/cardstatus/html/common`;
	
	// 하단 Swiper Controller
	const [subControlledSwiper, setSubControlledSwiper] = useState<SwiperCore>();
	const [swiperIndex, setSwiperIndex] = useHistoryState(props.swiperIndex, "swiperIndex");
	
	/** 체크카드회원번호 */
	const [cardMemberNo, setCardMemberNo] = useState<string>(props.cardMemberNo);
	/** 카드목록 */
	const [cardInfoList, setCardInfoList] = useState<CardInfo[]>(props.cardInfoList);
	/** 활성화 탭목록 */
	const [tabList, setTabList] = useHistoryState<string[]>(props.tabList, "tabList");
	/** 배송지변경 */
	const [changeDelivery, setChangeDelivery] = useState<ChangeDelivery>();
	const [isChangeDeliveryModal, setChangeDeliveryModal] = useState(false);
	/** 수령지 재업로드 */
	const [reuploadIndex/*, setReuploadIndex*/] = useState<number>(0);
	const [isAddressReuploadModal, setAddressReuploadModal] = useState(false);
	/** 수령지 확인 */
	const [deliveryConfirm, setDeliveryConfirm] = useState<DeliveryConfirm>();
	const [isDeliveryConfirmModal, setDeliveryConfirmModal] = useState(false);
	/** 교통카드 메뉴버튼 Ref */
	const railplusRef = props.cardInfoList.map(() => createRef<any>());
	/** 월회비 내역 조회 */
	const [monthlyFeeHistory, setMonthlyFeeHistory] = useState<Array<MonthlyFeeHistory>>([]);
	/** 미납 총액 */
	const [nonInoutTotalBalance, setNonInoutTotalBalance] = useState<number>(0);
	/** 미납 정보 */
	const [nonInoutInfoDetail, setNonInoutInfoDetail] = useState<Array<NonInoutInfoDetail>>([]);
	/** 연말정산 App Version */
	const [incomeDeductionAppVersion, setIncomeDeductionVersion] = useState<number | null>(null);
	/** 미납금 납부 App Version */
	const [payPendingFeeVersion, setPayPendingFeeVersion] = useState<number | null>(null);
	/** 카드 재발급 */
	const [isReissueModal, setReissueModal] = useState<boolean>(false);
	/** 카드 재발급 영업점 안내 (Quick Card 한정) */
	const [isReissueNoticeModal, setReissueNoticeModal] = useState<boolean>(false);
	/** 카드 재발급시 해지 안내 */
	const [isTerminateNoticeForReissueModal, setTerminateNoticeForReissueModal] = useState<boolean>(false);
	/** 미납 */
	const [isNonInoutModal, setNonInoutModal] = useState<boolean>(false);
	/** 출금동의 */
	const [debitState, setDebitState] = useState<CardDebitState | null>(null);
	const [isModalView, setIsModalView] = useState<boolean>(false);
	/** 신분증 신규 업로드 모드 플래그 */
	const [isIdCardApply, setIsIdCardApply] = useState<boolean>(false);
  const [continueApplyReceiveInfo, setContinueApplyReceiveInfo] = useState<{
    receiveSeq?: string,
    receiveDate?: string,
    userEngName?: string,
    phoneNo?: string,
    sexDivisionCode?: string,
    birthDate?: string
  }>({});

	const [totalPeningFeeAmount, setTotalPeningFeeAmount] = useState<string>("0");
	
	// 모달 닫기
	const closeModal = useCallback(() => {
		setIsModalView(false);
	}, []);
	
	// 메인 카드목록 생성
	const reloadCardInfoList = useCallback(() => {
		showProgressWeb();
		client.get("/api/app/main", {params: location.state.query}).then((response) => {
			// 체크카드회원번호
			setCardMemberNo(response.data.response.cardMemberNo);
			
			// 보유카드목록
			setCardInfoList(response.data.response.cardList);
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [location]);
	
	/** 탭변경 */
	const changeTab = useCallback((index: number, value: string) => {
		let tabs = [...tabList];
		
		// 교통카드 활성화 상태에서 메뉴 재클릭시 이벤트 처리
		if (value === "1" && tabs[index] === value) {
			railplusRef[index].current.click();
			return;
		}
		
		tabs[index] = value;
		setTabList(tabs, true);
		localStorage.setItem('menuCode', value);
	}, [railplusRef, setTabList, tabList]);
	
	const [menuConnect, setMenuConnect] = useState<boolean>(true);
	
	useEffect(() => {
		if (menuConnect) {
			setMenuConnect(!menuConnect);
			if (location.state.query.menuCode) {
				if (location.state.query.menuCode === '0') {
					if (["11", "21"].includes(cardInfoList[location.state.swiperIndex].cardStatusCode)) {
						// delete location.state.query.menuCode;
						
						if (cardInfoList[location.state.swiperIndex].cardStatusCode === "11") {
							navigate('/manage/password/change', {
								state: {
									query: location.state.query,
									info: cardInfoList[location.state.swiperIndex],
									swiperIndex: location.state.swiperIndex,
									tabIndex: '3',
									from: location.state.from
								}
							});
						} else {
							localStorage.setItem('menuCode', '3');
						}
					}
				}
			} else {
				if (location.state.linkOpenMenu === 'benefitsTab') {
					navigate("/main/benefitsTab/" + location.state.cardProductCode,
						{
							state: {
								query: location.state.query,
								apply: {
									cardMemberNo: cardMemberNo,
									requestIssueDivisionCode: cardInfoList.length > 0 ? "12" : "11",
									linkOpenMenu: location.state.linkOpenMenu
								},
								from: location.state.from,
								swiperIndex: swiperIndex,
								tabIndex: "1",
								cardInfoListLength: cardInfoList.length
							}
						});
				}
			}
		}
	}, [menuConnect, cardInfoList, location.state, navigate, setTabList, swiperIndex, cardMemberNo]);
	
	/** 카드혜택 클릭 */
	const clickBenefit = useCallback((value: CardInfo) => {
		navigate("/main/benefitsTab/" + value.cardProductCode, {
			state: {
				query: location.state.query,
				apply: {cardMemberNo: cardMemberNo, requestIssueDivisionCode: cardInfoList.length > 0 ? "12" : "11"},
				from: location.pathname,
				swiperIndex: swiperIndex,
				tabIndex: "1",
				cardInfoListLength: cardInfoList.length
			}
		});
	}, [navigate, location, cardMemberNo, cardInfoList, swiperIndex]);

	/** 카드혜택 클릭 */
	const clickOldBenefit = useCallback((value: CardInfo) => {
		navigate("/main/oldBenefits/" + value.cardProductCode, {
			state: location.state
		})
	}, [navigate, location]);

	const clickCardUpgrade = useCallback((value: CardInfo) => {
		showProgressWeb();

		/** 고객정보 조회 */
		client.get("/api/app/apply/info", {params: location.state.query}).then((response) => {
			let result = response.data.response;

			if (response.data.code === "00") {
				navigate("/main/benefitsTab/" + value.cardProductCode, {
					state: {
						...location.state,
						query: location.state.query,
						apply: {
							cardMemberNo: value.cardMemberNo,
							requestIssueDivisionCode: value.cardStatusCode === "22" ? "24" : "21",
							cardProductCode: value.cardProductCode,
							userEngName: result.userEngName,
							phoneNo: result.phoneNo,
							sexDivisionCode: result.sexDivisionCode,
							birthDate: result.birthDate,
							foreignerDivisionCode: result.foreignerDivisionCode,
							beforeAlternativeCardNo: value.alternativeCardNo,
							isReissue: true,
							reissueReceiveYn: value.reissueReceiveYn,
							reissueFee: value.reissueFee,
							promotionRate: value.promotionRate,
							reissuePromotionFee: value.reissuePromotionFee,
							reissuePromotionYn: "Y"
						},
						from: location.pathname,
						swiperIndex: swiperIndex,
						tabIndex: tabList[swiperIndex],
						reissue: "Y",
						userInfo: debitState,
						upgrade: true,
						cardInfoListLength: cardInfoList.length
					}
				});
			} else {
				callAlertWeb("ERROR", response.data.code, response.data.message);
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [debitState, location.state, navigate, location.pathname, swiperIndex, tabList, cardInfoList.length]);
	
	/** 수령지변경 클릭 */
	const clickChangeDelivery = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		let index = Number.parseInt(e.currentTarget.dataset["index"]!);
		setChangeDelivery({...cardInfoList[index]});
		setChangeDeliveryModal(true);
	}, [cardInfoList]);
	
	/** 수령지변경 호출 */
	const callChangeDelivery = useCallback(() => {
		navigate("/apply/step6/method", {
			state: {
				query: location.state.query,
				apply: {
					receiveSeq: changeDelivery?.receiveSeq,
					receiveDate: changeDelivery?.receiveDate,
					isChangeDelivery: true
				},
				from: location.pathname,
				swiperIndex: swiperIndex,
				tabIndex: "1"
			}
		});
	}, [navigate, location, changeDelivery, swiperIndex]);
	
	/** 수령지변경 모달 닫기 */
	const closeChangeDeliveryModal = useCallback(() => {
		setChangeDeliveryModal(false);
	}, []);
	
	/** 신분증 재업로드 클릭 */
	const clickIdcardReupload = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const appVersion = parseInt(location.state.query.apiVersion);
    if (appVersion >= NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION) {
      // 신분증 재업로드 인터페이스 호출
      reUploadIDCardWeb();
    } else {
      let index = Number.parseInt(e.currentTarget.dataset["index"]!);
      navigate("/apply/step4", {
        state: {
          query: location.state.query,
          apply: {
            receiveSeq: cardInfoList[index].receiveSeq,
            receiveDate: cardInfoList[index].receiveDate,
            foreignerDivisionCode: cardInfoList[index].foreignerDivisionCode,
            isReupload: true
          },
          from: location.pathname,
          swiperIndex: index,
          tabIndex: "1"
        }
      });
    }
	}, [navigate, location, cardInfoList]);
	
	/** 수령지 재업로드 클릭 */
	// const clickAddressReupload = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
	//   setReuploadIndex(Number.parseInt(e.currentTarget.dataset["index"]!));
	//   // 주소촬영 요청 (uploadPhotoAddress_Web (WEB > APP))
	//   uploadPhotoAddressWeb();
	// }, []);
	
	/** 주소촬영 결과전달 (completedPhotoAddress_App (APP > WEB)) */
	const callbackCompletedPhotoAddress = useCallback((event) => {
		showProgressWeb();
		client.post("/api/app/apply/address/reupload", {
			...location.state.query,
			receiveSeq: cardInfoList[reuploadIndex].receiveSeq,
			receiveDate: cardInfoList[reuploadIndex].receiveDate,
			thirdPartyReceiptYn: event.detail.receiveRelationCheck,
			addressImageValue: decodeURIComponent(event.detail.imageValue),
			detailAddr: event.detail.detailAddr
		}).then((response) => {
			if (response.data.code === "00") {
				setAddressReuploadModal(true);
			} else {
				callAlertWeb("ERROR", response.data.code, response.data.message);
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [location, cardInfoList, reuploadIndex]);
	
	/** 수령지 재업로드 모달 닫기 */
	const closeAddressReuploadModal = useCallback(() => {
		setAddressReuploadModal(false);
		// 메인 리로드
		reloadCardInfoList();
	}, [reloadCardInfoList]);
	
	// TODO 테스트용
	// const testCall = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
	//   setReuploadIndex(Number.parseInt(e.currentTarget.dataset["index"]!));
	//   let url = `${process.env.REACT_APP_LOCALHOST}/idcard-upload-1.png`;
	//   client.get(url, { responseType: 'arraybuffer' }).then((response) => {
	//     let imageValue = Buffer.from(response.data, "base64").toString("base64");
	//     let event = new CustomEvent("addressUpload", { detail: { receiveRelationCheck: "Y", imageValue: encodeURIComponent(imageValue) }});
	//     document.dispatchEvent(event);
	//   });
	// }, []);
	
	/** 수령지확인 클릭 */
	const clickDeliveryConfirm = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		let index = Number.parseInt(e.currentTarget.dataset["index"]!);
		setDeliveryConfirm({...cardInfoList[index]});
		setDeliveryConfirmModal(true);
	}, [cardInfoList]);
	
	/** 수령지확인 모달 닫기 */
	const closeDeliveryConfirmModal = useCallback(() => {
		setDeliveryConfirmModal(false);
	}, []);
	
	/** 사용등록 클릭 */
	const clickUseRegist = useCallback((value: CardInfo) => {
		navigate("/manage/card/verify/useRegist", {state: {query: location.state.query, info: value}});
	}, [navigate, location]);
	
	/** 월회비미납 클릭 */
	/* 2023.03.08 dhbyun
	const clickNonPaymentFee = useCallback((e: React.MouseEvent<HTMLParagraphElement, MouseEvent>, value: CardInfo) => {
	  e.stopPropagation();
	  navigate("/approval/nonPaymentFee", { state: { query: location.state.query, info: { alternativeCardNo: value.alternativeCardNo }}})
	}, [navigate, location]);
	*/
	
	/** 메인 리로딩 */
	const callbackReload = useCallback(() => {
		reloadCardInfoList();
	}, [reloadCardInfoList]);
	
	/** 카드재발급 모달닫기 */
	const closeReissueModal = useCallback(() => {
		setReissueModal(false);
		callbackReload();
	}, [callbackReload]);
	
	/** 미입금정보 모달닫기 */
	const closeNonInoutModal = useCallback(() => {
		if (isAndroid && window.appInterface) {
			props.callMainNativeWeb("Y");
		}
		setNonInoutModal(false);
	}, [props]);
	
	const callbackBackkeyEvent = useCallback(() => {
		if (isNonInoutModal) {
			closeNonInoutModal();
		}
	}, [isNonInoutModal, closeNonInoutModal]);
	
	/** 월회비납부내역조회 */
	const callMonthlyFeeHistory = useCallback(async (value: CardInfo) => {
			await client.get('/api/app/manage/monthlyFee/history', {
				params: {
					...location.state.query,
					alternativeCardNo: value.alternativeCardNo
				}
			}).then(response => {
				setMonthlyFeeHistory(response.data.response || []);
			}).catch((e: AxiosError) => {
				callAlertWeb('ERROR', '99', e.message);
			});
		},
		[location.state.query]
	);
	
	/** 월회비 납부 내역(미납) */
	const clickMonthlyFeeHistory = useCallback((value: CardInfo) => {
		navigate("/manage/card/pendingFee", {
			state: {
				query: location.state.query,
				info: value,
				swiperIndex: swiperIndex,
				storage: {
					monthlyFeeHistory: monthlyFeeHistory,
					nonInoutTotalBalance: nonInoutTotalBalance,
					nonInoutInfoDetail: nonInoutInfoDetail
				}
			}
		})
	}, [navigate, location.state.query, swiperIndex, monthlyFeeHistory, nonInoutTotalBalance, nonInoutInfoDetail]);
	
	/** 카드 일시정지 */
	const clickFreeze = useCallback((value: CardInfo) => {
			navigate("/manage/card/freeze",
				{
					state: {
						query: location.state.query
						, info: value
						, swiperIndex: swiperIndex
					}
				}
			)
		},
		[navigate, location.state.query, swiperIndex]
	);

	const openReissueModal = useCallback((value: CardInfo) => {
		switch (Number(value.cardProductCode)) {
			case CardProductCode.QuickCard:
				console.log("QUICK CARD 재발급 클릭");
				setReissueNoticeModal(true);
				break;
			default:
        // 만료 일자는 YYYYMM의 마지막 일자이며 moment()의 일은 1일로 초기화
        let validateTermYm = moment(value.validateTermYm, "YYYYMM");
        // 만료 2개월 일자 계산
        validateTermYm.subtract(1, 'months');

        // 유효기간 2개월 이내로 남아있는 경우 재발급 불가 > 해지 후 신청 안내
        if (validateTermYm.isSameOrBefore(moment())) {
          setTerminateNoticeForReissueModal(true);
        } else {
          setReissueModal(true);
        }
				break;
		}
	}, []);
	
	/** 재발급 클릭 */
	const clickReissue = useCallback(() => {
		showProgressWeb();
		
		/** 고객정보 조회 */
		client.get("/api/app/apply/info", {params: location.state.query}).then((response) => {
			let result = response.data.response;
			
			if (response.data.code === "00") {
				navigate("/apply/cardDesignSelect", {
					state: {
						query: location.state.query,
						apply: {
							cardMemberNo: cardInfoList[swiperIndex].cardMemberNo,
							requestIssueDivisionCode: cardInfoList[swiperIndex].cardStatusCode === "22" ? "24" : "21",
							cardProductCode: cardInfoList[swiperIndex].cardProductCode,
							userEngName: result.userEngName,
							phoneNo: result.phoneNo,
							sexDivisionCode: result.sexDivisionCode,
							birthDate: result.birthDate,
							foreignerDivisionCode: result.foreignerDivisionCode,
							beforeAlternativeCardNo: cardInfoList[swiperIndex].alternativeCardNo,
							isReissue: true
						},
						from: location.pathname,
						swiperIndex: swiperIndex,
						tabIndex: tabList[swiperIndex],
						reissue: "Y"
					}
				});
			} else {
				callAlertWeb("ERROR", response.data.code, response.data.message);
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [location, navigate, cardInfoList, swiperIndex, tabList]);
	
	// 카드 미납금 화면 이동
	const moveToPendingFee = useCallback((value: CardInfo) => {
		navigate("/manage/card/pendingFee", {
			state: {
				query: location.state.query,
				info: value,
				swiperIndex: swiperIndex,
				storage: {
					monthlyFeeHistory: monthlyFeeHistory,
					nonInoutTotalBalance: nonInoutTotalBalance,
					nonInoutInfoDetail: nonInoutInfoDetail
				}
			}
		})
	}, [navigate, location.state.query, swiperIndex, monthlyFeeHistory, nonInoutTotalBalance, nonInoutInfoDetail]);
	
	
	/** 미납항목조회 */
	const callNonInoutInfo = useCallback(async () => {
			await client.get('/api/app/manage/nonInout/info', {
				params: {
					...location.state.query
				}
			}).then(response => {
				setNonInoutTotalBalance(Number(response.data.response?.totalBalance) || 0);
				setNonInoutInfoDetail(response.data.response?.detail || []);
			}).catch((e: AxiosError) => {
				callAlertWeb('ERROR', '99', e.message);
			});
		},
		[location.state.query]
	);
	
	/**
	 * dhbyun
	 * CARD_INCOME_DEDUCTION 조회
	 */
	const cardIncomeDeductionVersionApi = useCallback(
		async () => {
			await inquiryFunctionAppVersion("CARD_INCOME_DEDUCTION", location)
				.then((res) => {
					if (res.data.resData) {
						setIncomeDeductionVersion(res.data.resData.version);
					}
				}).catch((e: AxiosError) => {
					callAlertWeb("ERROR", "99", e.message);
				});
		},
		[location]
	);
	
	/**
	 * dhbyun
	 * CARD_PAY_PENDING_FEE 조회
	 */
	const cardPayPendingFeeVersionApi = useCallback(
		async () => {
			await inquiryFunctionAppVersion("CARD_PAY_PENDING_FEE", location)
				.then((res) => {
					if (res.data.resData) {
						setPayPendingFeeVersion(res.data.resData.version);
					}
				}).catch((e: AxiosError) => {
					callAlertWeb("ERROR", "99", e.message);
				});
		},
		[location]
	);
	
	/** 출금동의여부조회 */
	const callDebitState = useCallback(async () => {
		await client.get('/api/app/manage/debit/state', {
			params: {
				...location.state.query
			}
		}).then(response => {
			setDebitState(response.data.response);
		}).catch((e: AxiosError) => {
			callAlertWeb('ERROR', '99', e.message);
		});
	}, [location.state.query]);

	const clickQuickCardRegister = useCallback(() => {
		if (Number(location.state.query.apiVersion) >= QUICK_CARD_REGISTER_MIN_VERSION) {
			clickMoveToAppWeb("N876");
		} else {
			callAlertWeb("99", "99", t('STAT01.popup05'));
		}
	}, [location.state.query.apiVersion, t]);
	
	/** 카드신청계속 클릭 */
	const clickApplyContinue = useCallback((cardApplyNotify: CardApplyNotify) => {
		switch (Number(cardApplyNotify.designDivisionCode)) {
			case CardProductCode.QuickCard:
				clickQuickCardRegister();
				break;
			default:
        showProgressWeb();
        /** 고객정보 조회 */
        client.get("/api/app/apply/info", {
          params: {
            ...location.state.query,
            cardProductCode: cardApplyNotify?.cardProductCode
          }
        }).then((response) => {
          let result = response.data.response;

          if (response.data.code === "00") {
            if (cardApplyNotify.receiveStatusCode === "04") {
              const appVersion = parseInt(location.state.query.apiVersion);
              if (appVersion >= NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION) {
                // 신규 버전: 네이티브 신분증 업로드 호출
                setIsIdCardApply(true);
                setContinueApplyReceiveInfo({
                  receiveSeq: cardApplyNotify.receiveSeq,
                  receiveDate: cardApplyNotify.receiveDate,
                  userEngName: result.userEngName,
                  phoneNo: result.phoneNo,
                  sexDivisionCode: result.sexDivisionCode,
                  birthDate: result.birthDate
                });
                uploadIDCardWeb();
                return;
              }
              // 구버전: 핀코드 입력 완료 단계는 신분증 업로드 단계로 이동한다.
              navigate("/apply/step4", {
                state: {
                  query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode},
                  apply: {
                    ...location.state.apply,
                    cardProductCode: cardApplyNotify?.cardProductCode,
                    receiveSeq: cardApplyNotify.receiveSeq,
                    receiveDate: cardApplyNotify.receiveDate,
                    foreignerDivisionCode: result.foreignerDivisionCode,
                    isReupload: false,
                    isChangeDelivery: false,
                    userEngName: result.userEngName,
                    phoneNo: result.phoneNo,
                    sexDivisionCode: result.sexDivisionCode,
                    birthDate: result.birthDate
                  },
                  from: location.state.from,
                  swiperIndex: swiperIndex,
                  tabIndex: "1"
                }
              });
            } else if (cardApplyNotify.receiveStatusCode === "05") {
              // 신분증 업로드 완료 단계는 고객정보등록 단계로 이동한다.
              navigate("/apply/step5", {
                state: {
                  ...location.state,
                  query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode},
                  apply: {
                    ...location.state.apply,
                    cardProductCode: cardApplyNotify?.cardProductCode,
                    receiveSeq: cardApplyNotify.receiveSeq,
                    receiveDate: cardApplyNotify.receiveDate,
                    isChangeDelivery: false,
                    designDivisionCode: cardApplyNotify.designDivisionCode,
                    userEngName: result.userEngName,
                    phoneNo: result.phoneNo,
                    sexDivisionCode: result.sexDivisionCode,
                    birthDate: result.birthDate,
                    foreignerDivisionCode: result.foreignerDivisionCode
                  },
                  userInfo: debitState
                }
              });
            } else if (cardApplyNotify.receiveStatusCode === "06") {
              // 카드수령지 선택 단계는 수령지 선택으로 이동한다.
              navigate("/apply/step6/method", {
                state: {
                  ...location.state,
                  query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode},
                  apply: {
                    ...location.state.apply,
                    cardProductCode: cardApplyNotify?.cardProductCode,
                    receiveSeq: cardApplyNotify.receiveSeq,
                    receiveDate: cardApplyNotify.receiveDate,
                    isChangeDelivery: false,
                    designDivisionCode: cardApplyNotify.designDivisionCode
                  },
                  userInfo: debitState
                }
              });
            }
            // 그 이전단계는 처음부터 시작한다.
            else {
              navigate("/apply/step0", {
                state: {
                  ...location.state,
                  query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode},
                  apply: {
                    ...location.state.apply,
                    cardProductCode: cardApplyNotify.cardProductCode,
                    requestIssueDivisionCode: cardInfoList.length > 0 ? "12" : "11",
                    userEngName: result.userEngName,
                    phoneNo: result.phoneNo,
                    sexDivisionCode: result.sexDivisionCode,
                    birthDate: result.birthDate,
                    foreignerDivisionCode: result.foreignerDivisionCode
                  },
                  userInfo: debitState
                }
              });
            }
          } else {
            callAlertWeb("ERROR", response.data.code, response.data.message);
          }
        }).catch((e: AxiosError) => {
          callAlertWeb("ERROR", "99", e.message);
        }).finally(() => {
          hideProgressWeb();
        });
				break;
		}
	}, [navigate, location, debitState, cardInfoList, clickQuickCardRegister, swiperIndex]);
	
	/** 카드신청 클릭 */
	const clickApply = useCallback(async (cardInfo: CardInfo) => {
		showProgressWeb();
		
		// 이전 접수내역 확인
		await client.get("/api/app/product/check", {
			params: {
				...location.state.query,
				cardProductCode: cardInfo.cardProductCode
			}
		}).then(response => {
			let result = response.data.response;
			
			if (response.data.code === "00") {
				// 이전 접수내역이 있을 경우
				if (result.receiveStatusNotifyCode === "Y") {
					clickApplyContinue(result);
				} else {
					/** 고객정보 조회 */
					client.get("/api/app/apply/info", {
						params: {
							...location.state.query,
							cardProductCode: cardInfo.cardProductCode
						}
					}).then((response) => {
						let result = response.data.response;
						
						if (response.data.code === "00") {
							// KYC 미인증
							if (result.kycApproved === "N") {
								callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
							} else {
								switch (Number(cardInfo.cardProductCode)) {
									case CardProductCode.QuickCard:
										clickQuickCardRegister();
										break;
									default:
										if (cardInfo.membershipFeeCode === '1') {
											if (result.totalPendingFeeAmount === "0") {
												navigate("/apply/step0", {
													state: {
														...location.state,
														query: {
															...location.state.query,
															cardProductCode: cardInfo.cardProductCode
														},
														apply: {
															...location.state.apply,
															cardProductCode: cardInfo.cardProductCode,
															requestIssueDivisionCode: cardInfoList.length > 0 ? "12" : "11",
															userEngName: result.userEngName,
															phoneNo: result.phoneNo,
															sexDivisionCode: result.sexDivisionCode,
															birthDate: result.birthDate,
															foreignerDivisionCode: result.foreignerDivisionCode
														},
														userInfo: debitState
													}
												});
											} else {
												setTotalPeningFeeAmount(result.totalPendingFeeAmount);
												setIsModalView(true);
											}
										} else {
											navigate("/apply/step0", {
												state: {
													...location.state,
													query: {...location.state.query, cardProductCode: cardInfo.cardProductCode},
													apply: {
														...location.state.apply,
														cardProductCode: cardInfo.cardProductCode,
														requestIssueDivisionCode: cardInfoList.length > 0 ? "12" : "11",
														userEngName: result.userEngName,
														phoneNo: result.phoneNo,
														sexDivisionCode: result.sexDivisionCode,
														birthDate: result.birthDate,
														foreignerDivisionCode: result.foreignerDivisionCode
													},
													userInfo: debitState
												}
											});
										}
										break;
								}
							}
						} else {
							callAlertWeb("ERROR", response.data.code, response.data.message);
						}
					}).catch((e: AxiosError) => {
						callAlertWeb("ERROR", "99", e.message);
					});
				}
			} else {
				callAlertWeb("ERROR", response.data.code, response.data.message);
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [location, navigate, clickApplyContinue, t, debitState, setIsModalView, cardInfoList, clickQuickCardRegister]);
	
	/* 카드 해지 신청 취소 */
	const cardTerminateCancel = useCallback((value: CardInfo) => {
		navigate('/manage/pincode/verify', {
			state: {
				query: location.state.query,
				swiperIndex: swiperIndex,
				tabIndex: tabList[swiperIndex],
				info: value,
				apply: {
					alternativeCardNo: value.alternativeCardNo,
					callPage: "cardTerminateCancel"
				},
				from: location.pathname,
				storage: {
					terminate: {
						alternativeCardNo: value.alternativeCardNo
					}
				}
			}
		});
	}, [navigate, location, swiperIndex, tabList]);
	
	// 주소촬영 결과전달 이벤트 리스너 (completedPhotoAddress_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("addressUpload", callbackCompletedPhotoAddress);
		return () => document.removeEventListener("addressUpload", callbackCompletedPhotoAddress);
	}, [callbackCompletedPhotoAddress]);
	
	useEffect(() => {
		const currentCardInfo = cardInfoList[swiperIndex];
		if (currentCardInfo && cardInfoList[swiperIndex].cardApplyYn !== undefined && cardInfoList[swiperIndex].cardApplyYn === 'Y') {
			callMonthlyFeeHistory(cardInfoList[swiperIndex])
		}
	}, [cardInfoList, swiperIndex, callMonthlyFeeHistory]);
	
	useEffect(() => {
		// 상단 Swiper 회색 이슈 처리
		window.scrollTo(0, 1);
		
		callNonInoutInfo();
		cardIncomeDeductionVersionApi();
		cardPayPendingFeeVersionApi();
		
		callDebitState();
		
		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener('backpress', callbackBackkeyEvent);
		return () => document.removeEventListener('backpress', callbackBackkeyEvent);
	}, [callbackBackkeyEvent]);

  const callbackIDCardReUpload = useCallback(async (event) => {
    // App 에서 넘어온 신분증 이미지 세팅
    let idcardList = { [event.detail.idType + event.detail.photoType]: decodeURIComponent(event.detail.imageValue) };

    // 신분증구분코드 세팅
    // 1: 주민등록증
    // 3: 외국인 등록증
    // 4: 여권
    let idcardDivisionCode = event.detail.idType;

    showProgressWeb();

    if (isIdCardApply) {
      // 신규 신청 경로: callbackIDCardUpload() 와 동일 동작
      await client.post("/api/app/apply/idcard/upload", { ...location.state.query, receiveSeq: continueApplyReceiveInfo.receiveSeq, receiveDate: continueApplyReceiveInfo.receiveDate, idcardDivisionCode: idcardDivisionCode, idcardList: idcardList }).then((response) => {
        if (response.data.code === "00") {
          navigate("/apply/step5", {
            state: { ...location.state,
              apply: {
              ...location.state.apply,
                receiveSeq: continueApplyReceiveInfo.receiveSeq,
                receiveDate: continueApplyReceiveInfo.receiveDate,
                userEngName: continueApplyReceiveInfo.userEngName,
                phoneNo: continueApplyReceiveInfo.phoneNo,
                sexDivisionCode: continueApplyReceiveInfo.sexDivisionCode,
                birthDate: continueApplyReceiveInfo.birthDate
              },
              from: location.pathname
            }
          });
        }
        else {
          callAlertWeb("ERROR", response.data.code, response.data.message);
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      }).finally(() => {
        hideProgressWeb();
        initContinueApplyReceiveInfo();
      });
    } else {
      // 재업로드 경로: 기존과 동일
      await client.post("/api/app/apply/idcard/reupload", { ...location.state.query, receiveSeq: cardInfoList[swiperIndex].receiveSeq, receiveDate: cardInfoList[swiperIndex].receiveDate, idcardDivisionCode, idcardList }).then((response) => {
        if (response.data.code === "00") {
          // 성공 시 메인 리로드
          reloadCardInfoList();
        } else {
          callAlertWeb("ERROR", response.data.code, response.data.message);
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      }).finally(() => {
        hideProgressWeb();
        initContinueApplyReceiveInfo();
      });
    }
  }, [isIdCardApply, cardInfoList, location.pathname, location.state, navigate, reloadCardInfoList, swiperIndex, continueApplyReceiveInfo]);


  // 신분증 업로드 이벤트 리스너 (_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("idUpload", callbackIDCardReUpload);
    return () => document.removeEventListener("idUpload", callbackIDCardReUpload);
  }, [callbackIDCardReUpload]);
	
	const swiperTabChangeEventHandle = () => {
		subControlledSwiper?.updateAutoHeight(1);
	}
	
	// 신분증, 주소지 재업로드 요청 확인
	const reUploadCheck = (reuploadIdcardCount: number, reuploadAddressCount: number) => {
		if ((reuploadIdcardCount > 0 || reuploadAddressCount > 0)) {
			return true;
		}
		return false;
	}

  const initContinueApplyReceiveInfo = () => {
    setIsIdCardApply(false);
    setContinueApplyReceiveInfo({});
  }
	
	return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        {/* 상단 Swiper Start */}
        <Swiper
          modules={[Controller, Pagination, A11y]}
          className="swiper-container3"
          slidesPerView={'auto'}
          centeredSlides={true}
          spaceBetween={9}
          pagination={{ el: '.swiper-pagination3', clickable: true }}
          controller={{ control: subControlledSwiper! }}
          initialSlide={swiperIndex}
          onSlideChange={(swiper) => setSwiperIndex(swiper.activeIndex, true)}>
          {cardInfoList.map((value, index) => (
            <SwiperSlide key={`${value.cardProductCode}${index}top`}>
              {value.cardApplyYn !== undefined && value.cardApplyYn === 'N' && (
                <CardApplyContainer cardInfo={value} />
              )}
              {value.cardApplyYn !== undefined && value.cardApplyYn === 'Y' && (
                <CardContainer
                  cardInfo={value}
                  nonInoutTotalBalance={nonInoutTotalBalance}
                  incomeDeductionAppVersion={incomeDeductionAppVersion}
                  clickBenefit={clickBenefit}
				  				clickOldBenefit={clickOldBenefit}
				  				clickCardUpgrade={clickCardUpgrade}
                  clickMonthlyFeeHistory={clickMonthlyFeeHistory}
                  clickFreeze={clickFreeze}
                  openReissueModal={openReissueModal}
                  callMainNativeWeb={props.callMainNativeWeb}
                  moveToPendingFee={moveToPendingFee}
                  cardTerminateCancel={cardTerminateCancel}
  			 	  			debitState={debitState}
                />
              )}
              {/* 미접수 */}
              {value.cardApplyYn !== undefined && value.cardApplyYn === 'N' && (
                <div className="btnWrapTy04 ea1">
                  <button
                    type="button"
                    className="btnType05 on"
                    onClick={() => clickApply(value)}
                    data-index={index}>
                    <span>{Number(value.cardProductCode) === CardProductCode.QuickCard ? t('STAT01.TEXT_3') : t('APPL01.apply')}</span>
                  </button>
                </div>
              )}
              {/* 접수 - 미납금이 있는 경우 */}
              {value.cardStatusCode === '01' &&
                value.issueFee > 0 &&
                value.cardProductCode === '103079' && (
                  <div className="btnWrapTy04 ea1">
                    <button
                      type="button"
                      className="btnType05 on"
                      onClick={() => clickMoveToAppWeb('N039')}
                      data-index={index}>
                      <span>{t('APPL22.title')}</span>
                    </button>
                  </div>
                )}
              {/* 접수 - 미납금이 없고 업로드할 이미지가 아직 없는 경우 */}
              {value.cardStatusCode === '01' &&
                value.reuploadIdcardCount === 0 &&
                value.reuploadAddressCount === 0 && (
                  <div className="btnWrapTy04 ea2">
                    <button
                      type="button"
                      className="btnType04 on"
                      onClick={() => phoneCallWeb('1670-4565')}>
                      <i className="ico-call"></i>
                      <span>{t('STAT01.button01')}</span>
                    </button>
                    <button
                      type="button"
                      className="btnType04 on"
                      onClick={clickChangeDelivery}
                      data-index={index}
                      disabled={value.deliverySelectCode === '1'}>
                      <i className="ico-home"></i>
                      <span>{t('STAT01.button02')}</span>
                    </button>
                  </div>
                )}
              {/* 접수 - 미납금이 없고 업로드할 이미지가 있는 경우 */}
              {value.cardStatusCode === '01' &&
                (value.reuploadIdcardCount > 0 ||
                  value.reuploadAddressCount > 0) && (
                  <div
                    className={
                      value.reuploadIdcardCount > 0 &&
                      value.reuploadAddressCount > 0
                        ? 'btnWrapTy04 ea2'
                        : 'btnWrapTy04 ea1'
                    }>
                    {/* 신분증 업로드 */}
                    {value.reuploadIdcardCount > 0 && (
                      <button
                        type="button"
                        className="btnType04 on"
                        onClick={clickIdcardReupload}
                        data-index={index}>
                        <i className="ico-camera"></i>
                        <span>{t('STAT03.button01')}</span>
                      </button>
                    )}
                    {/* 수령지 업로드 */}
                    {value.reuploadAddressCount > 0 && (
                      <button
                        type="button"
                        className="btnType04 on"
                        onClick={clickChangeDelivery}
                        data-index={index}>
                        <i className="ico-camera"></i>
                        <span>{t('STAT01.button02')}</span>
                      </button>
                    )}
                  </div>
                )}
              {/* 배송중 */}
              {value.cardStatusCode === '02' && (
                <div className="btnWrapTy04 ea1">
                  <button
                    type="button"
                    className="btnType04 on"
                    onClick={clickDeliveryConfirm}
                    data-index={index}>
                    <i className="ico-home"></i>
                    <span>{t('STAT04.button01')}</span>
                  </button>
                </div>
              )}
              {/* 사용등록전 */}
              {value.cardStatusCode === '03' && (
                <div className="btnWrapTy04 ea1">
                  <button
                    type="button"
                    className="btnType04 on"
                    onClick={() => clickUseRegist(value)}
                    data-index={index}>
                    <i className="ico-check"></i>
                    <span>{t('STAT05.button01')}</span>
                  </button>
                </div>
              )}
              {/* 정상, 일시정지, 분실, 기타 */}
              {['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                <div className="btnWrapTy04 ea3">
                  <button
                    type="button"
                    className={
                      tabList[index] === '1' ? 'btnType04 on' : 'btnType04'
                    }
                    onClick={() => changeTab(index, '1')}
                    disabled={location.state.query.trafficYn === 'N'}>
                    <i className="ico-train"></i>
                    <span>{t('TRAN01.button02')}</span>
                  </button>
                  <button
                    type="button"
                    className={
                      tabList[index] === '2' ? 'btnType04 on' : 'btnType04'
                    }
                    onClick={() => changeTab(index, '2')}>
                    <i className="ico-list"></i>
                    <span>{t('TRAN01.button01')}</span>
                  </button>
                  <button
                    type="button"
                    className={
                      tabList[index] === '3' ? 'btnType04 on' : 'btnType04'
                    }
                    onClick={() => changeTab(index, '3')}>
                    <i className="ico-manage"></i>
                    <span>{t('TRAN01.button03')}</span>
                  </button>
                </div>
              )}
            </SwiperSlide>
          ))}
          {/* 고정 */}
          {/*
          <SwiperSlide>
            <div className="cardWrap4" onClick={clickApply}>
              <div className="add_card">
                <img src="/assets/images/ico/icon-black-cross.png" alt="icon-black-cross.png"/>
                <p>{t("STAT02.button02")}</p>
              </div>
            </div>
            <div className="btnWrapTy04 ea1">
              <button type="button" className="btnType04 on" onClick={clickApplyTab}><i className="ico-card"></i><span><b>{t("STAT02.button01")}</b></span></button>
            </div>
          </SwiperSlide>
          */}
          {/* Add Pagination */}
          <div className="swiper-pagination3"></div>
        </Swiper>
        {/* 상단 Swiper End */}
        {/* 하단 Swiper Start */}
        <Swiper
          modules={[A11y]}
          autoHeight={true}
          className="swiper-no-swiping"
          onSwiper={(swiper) => setSubControlledSwiper(swiper)}>
          {cardInfoList.map((value, index) => (
            <SwiperSlide key={`${value.cardProductCode}${index}bottom`}>
              {/* 미접수 상태 */}
              {value.cardApplyYn !== undefined && value.cardApplyYn === 'N' && (
                <CardProductBenefitHTML
                  langCode={location.state.query.languageCode}
                  cardProductCode={value.cardProductCode}
                />
              )}

              {/* 접수 상태 이력이 없을 경우 */}
              {isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardApplyYn !== undefined &&
                value.cardApplyYn === 'Y' &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardProductBenefitHTML
                    langCode={location.state.query.languageCode}
                    cardProductCode={value.cardProductCode}
                  />
                )}

              {/* Processing 상태 - 미납금이 있는 경우 */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'processing' &&
                value.issueFee > 0 &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_processing_issueFee.html?lang=${
                      location.state.query.languageCode
                    }&data=${
                      value.cardProductCodeName
                    }||${StringUtils.moneyFormat(
                      StringUtils.nvlDefault(value.issueFee, '0')
                    )}`}
                  />
                )}

              {/* Processing 상태 (영업점 수령일 경우) */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'processing' &&
                value.deliverySelectCode === '2' &&
                value.issueFee < 1 &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) &&
                !reUploadCheck(
                  value.reuploadIdcardCount,
                  value.reuploadAddressCount
                ) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_processing_visitBranch.html?lang=${location.state.query.languageCode}`}
                  />
                )}

              {/* Processing 상태 (주소지 수령일 경우) */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'processing' &&
                value.deliverySelectCode !== '2' &&
                value.issueFee < 1 &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) &&
                !reUploadCheck(
                  value.reuploadIdcardCount,
                  value.reuploadAddressCount
                ) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_processing_checking.html?lang=${location.state.query.languageCode}`}
                  />
                )}

              {/* Processing 상태 (주소지, id 변경이 필요할 경우) */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'processing' &&
                value.issueFee < 1 &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) &&
                reUploadCheck(
                  value.reuploadIdcardCount,
                  value.reuploadAddressCount
                ) && (
                  <div>
                    <iframe
                      className="cardStatusBar"
                      src={`${outerUrl}/card_processing_status_common.html?lang=${location.state.query.languageCode}&v=${WEBVIEW_RESOURCE_VERSION}`}
                      title={'card status'}
                    />
                    <CardReuploadHTML
                      pushId={value.pushId}
                      pushAddr={value.pushAddr}
                    />
                  </div>
                )}

              {/* Delivery 배송중상태 */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'delivery' &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_delivery.html?lang=${
                      location.state.query.languageCode
                    }&data=${
                      value.homeNewAddress + ' ' + value.homeNewDetailAddress
                    }||${value.phoneNo}`}
                  />
                )}

              {/* Branch 영업점 배송완료상태 */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'branch' &&
                value.deliverySelectCode === '2' &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_visitBranch.html?lang=${
                      location.state.query.languageCode
                    }&data=${value.deliveryBranchCodeName}||${
                      value.deliveryBranchCodeNameEn
                    }||${
                      value.homeNewAddress + ' ' + value.homeNewDetailAddress
                    }||${value.phoneNo}`}
                  />
                )}

              {/* Redelivery 재배송상태 (영업점 수령일경우)*/}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'redelivery' &&
                value.deliverySelectCode === '2' &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_redelivery_visitBranch.html?lang=${
                      location.state.query.languageCode
                    }&data=${
                      value.homeNewAddress + ' ' + value.homeNewDetailAddress
                    }||${value.cardProductCodeName}`}
                  />
                )}

              {/* Redelivery 재배송상태 (주소지 수령일경우)*/}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'redelivery' &&
                value.deliverySelectCode !== '2' &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_redelivery.html?lang=${
                      location.state.query.languageCode
                    }&data=${
                      value.homeNewAddress + ' ' + value.homeNewDetailAddress
                    }||${value.cardProductCodeName}`}
                  />
                )}

              {/* Returned 반송 상태 */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'returned' &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_returned.html?lang=${
                      location.state.query.languageCode
                    }&data=${
                      value.homeNewAddress + ' ' + value.homeNewDetailAddress
                    }||${value.cardProductCodeName}`}
                  />
                )}

              {/* Activate 고객주소 배송완료 상태 */}
              {!isNullOrBlank(value.cardIssueStatusDisplayCode) &&
                value.cardIssueStatusDisplayCode === 'delivered' &&
                !['11', '21', '22', '90'].includes(value.cardStatusCode) && (
                  <CardStatusHTML
                    title={value.cardProductCode}
                    url={`${outerUrl}/card_activate.html?lang=${
                      location.state.query.languageCode
                    }&data=${
                      value.homeNewAddress + ' ' + value.homeNewDetailAddress
                    }||${value.cardProductCodeName}`}
                  />
                )}

              {/* 정상, 일시정지, 분실, 기타 - 교통카드 */}
              {['11', '21', '22', '90'].includes(value.cardStatusCode) &&
                tabList[index] === '1' && (
                  <RailplusGuide
                    cardInfo={value}
                    callbackReload={callbackReload}
                    swiperIndex={index}
                    swiper={subControlledSwiper}
                    ref={railplusRef[index]}
                    swiperTabChangeEventHandle={
                      swiperTabChangeEventHandle
                    }></RailplusGuide>
                )}
              {/* 정상, 일시정지, 분실, 기타 - 이용내역 */}
              {['11', '21', '22', '90'].includes(value.cardStatusCode) &&
                tabList[index] === '2' && (
                  <ApprovalList
                    cardInfo={value}
                    callbackReload={callbackReload}
                    swiperIndex={index}
                    swiperTabChangeEventHandle={
                      swiperTabChangeEventHandle
                    }></ApprovalList>
                )}
              {/* 정상, 일시정지, 분실, 기타 - 카드관리 */}
              {['11', '21', '22', '90'].includes(value.cardStatusCode) &&
                tabList[index] === '3' && (
                  <CardManage
                    cardInfo={value}
                    callbackReload={callbackReload}
                    swiperIndex={index}
                    linkOpenMenu={location.state.linkOpenMenu}
                    nonInoutTotalBalance={nonInoutTotalBalance}
                    nonInoutInfoDetail={nonInoutInfoDetail}
                    callNonInoutInfo={callNonInoutInfo}
                    incomeDeductionAppVersion={incomeDeductionAppVersion}
                    cardIncomeDeductionVersionApi={
                      cardIncomeDeductionVersionApi
                    }
                    callMonthlyFeeHistory={callMonthlyFeeHistory}
                    monthlyFeeHistory={monthlyFeeHistory}
                    swiperTabChangeEventHandle={swiperTabChangeEventHandle}
                    cardTerminateCancel={cardTerminateCancel}
                    openReissueModal={openReissueModal}
                  />
                )}
            </SwiperSlide>
          ))}
          {/* 고정 */}
          {/*
          <SwiperSlide>
            <section className="contents statWrap">
              <div className="statWrapSub">
                <div className="txtWrap">
                  <p className="mainTxt">{t("STAT02.explan01")}</p>
                  <p className="subTxt">{t("STAT02.explan02")}</p>
                  <p className="subTxt">{t("STAT02.explan03")}</p>
                  <p className="subTxt">{t("STAT02.explan04")}</p>
                </div>
              </div>
            </section>
          </SwiperSlide>
          */}
        </Swiper>
        {/* 하단 Swiper End */}
      </div>
      {/* 수령지변경 */}
      <Modal
        isOpen={isChangeDeliveryModal}
        contentLabel="modal"
        onRequestClose={closeChangeDeliveryModal}
        className="popTyLayer2"
        overlayClassName="dim"
        closeTimeoutMS={500}>
        <p className="message">{t('STAT01.popup02')}</p>
        <dl>
          <dt>{t('STAT01.popup03')}</dt>
          <dd>
            {t(`STAT01.popup04.${changeDelivery?.deliverySelectCode}`)}
            {changeDelivery?.deliverySelectCode === '2' && (
              <span>
                {' '}
                : {t(`BRANCH.name.${changeDelivery.deliveryBranchCode}`)}
              </span>
            )}
          </dd>
          {changeDelivery?.homeZipCode &&
            changeDelivery?.homeNewAddress &&
            changeDelivery?.homeNewDetailAddress && (
              <dd className="detail">
                ({changeDelivery.homeZipCode}) {changeDelivery.homeNewAddress}{' '}
                {changeDelivery.homeNewDetailAddress}
              </dd>
            )}
        </dl>
        <div className="btnWrapTy02">
          <button
            type="button"
            className="btnPrimaryTy03"
            onClick={closeChangeDeliveryModal}>
            <span>{t('COMMON.button.no')}</span>
          </button>
          <button
            type="button"
            className="btnPrimaryTy01"
            onClick={callChangeDelivery}>
            <span>{t('COMMON.button.yes')}</span>
          </button>
        </div>
      </Modal>
      {/* 수령지 재업로드 */}
      <Modal
        isOpen={isAddressReuploadModal}
        contentLabel="modal"
        onRequestClose={closeAddressReuploadModal}
        className="popTyLayer2"
        overlayClassName="dim"
        closeTimeoutMS={500}>
        <p className="message">{t('STAT03.popup03')}</p>
        <div className="btnWrapTy02">
          <button
            type="button"
            className="btnPrimaryTy01"
            onClick={closeAddressReuploadModal}>
            <span>{t('COMMON.button.confirm')}</span>
          </button>
        </div>
      </Modal>
      {/* 수령지 확인 */}
      <Modal
        isOpen={isDeliveryConfirmModal}
        contentLabel="modal"
        onRequestClose={closeDeliveryConfirmModal}
        className="popTyLayer2"
        overlayClassName="dim"
        closeTimeoutMS={500}>
        <p className="message">{t('STAT04.popup01')}</p>
        <dl>
          <dt>{t('STAT04.popup02')}</dt>
          <dd>
            <span>
              ({deliveryConfirm?.homeZipCode}) {deliveryConfirm?.homeNewAddress}
            </span>
          </dd>
          <dd className="detail">{deliveryConfirm?.homeNewDetailAddress}</dd>
        </dl>
        <div className="btnWrapTy02">
          <button
            type="button"
            className="btnPrimaryTy01"
            onClick={closeDeliveryConfirmModal}>
            <span>{t('COMMON.button.confirm')}</span>
          </button>
        </div>
      </Modal>
      {cardInfoList[swiperIndex] && (
        //카드재발급
        <Modal
          isOpen={isReissueModal}
          contentLabel="modal"
          onRequestClose={closeReissueModal}
          className="popTyLayer2"
          overlayClassName="dim"
          closeTimeoutMS={500}>
          <p className="message">{t('SERV06.popup02')}</p>
          <p className="subTxt mgt20">{t('SERV06.popup03')}</p>
          <p className="messageARS">
            {t('SERV06.popup05')} :{' '}
            <span className="error">
              {StringUtils.moneyFormat(cardInfoList[swiperIndex].reissueFee)}
            </span>{' '}
            KRW
          </p>
          <div className="btnWrapTy02">
            <button
              type="button"
              className="btnPrimaryTy01"
              onClick={closeReissueModal}>
              <span>{t('COMMON.button.no')}</span>
            </button>
            <button
              type="button"
              className="btnPrimaryTy03"
              onClick={clickReissue}
              disabled={
                cardInfoList[swiperIndex].reissueReceiveYn === 'Y' ||
                cardInfoList[swiperIndex].useYn !== 'Y'
              }>
              <span>{t('COMMON.button.yes')}</span>
            </button>
          </div>
        </Modal>
      )}
      {cardInfoList[swiperIndex] && (
        // 카드 재발급 영업점 안내
				<ReissueNoticeModal
					isOpen={isReissueNoticeModal}
					onClose={() => {
						setReissueNoticeModal(false);
					}}
					onGoToBranchListClick={() => {
						clickMoveToAppWeb("N464");
						setReissueNoticeModal(false);
					}}
				/>
			)}
			{nonInoutTotalBalance > 0 && nonInoutInfoDetail.length > 0 && (
				<Modal
					isOpen={isNonInoutModal}
					contentLabel="modal"
					onRequestClose={closeNonInoutModal}
					className="popTyLayer2 nonInoutModal"
					overlayClassName="dim"
					closeTimeoutMS={500}
					preventBackgroundScroll>
					<div>
						<div className="Please-pay-Pending-Fee">
							<span>{t('SERV16.explan01')}</span>
						</div>
            <div className="nonInoutContent">
              <NonInoutInfo
                callNonInoutInfo={callNonInoutInfo}
                nonInoutTotalBalance={nonInoutTotalBalance}
                nonInoutInfoDetail={nonInoutInfoDetail}
              />
            </div>
            <div className="btnWrapTy02">
              {location.state.query.apiVersion != null &&
              payPendingFeeVersion != null &&
              parseInt(location.state.query.apiVersion) >=
                payPendingFeeVersion &&
              nonInoutTotalBalance !== 0 ? (
                <button
                  type="button"
                  className="btnPrimaryTy01"
                  disabled={
                    location.state.query.apiVersion == null ||
                    payPendingFeeVersion == null ||
                    parseInt(location.state.query.apiVersion) <
                      payPendingFeeVersion!
                  }
                  onClick={() => clickMoveToAppWeb('N039')}>
                  <span>{t('SERV16.button.pay')}</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="btnPrimaryTy01"
                  onClick={closeNonInoutModal}>
                  <span>{t('COMMON.button.done')}</span>
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
      {cardInfoList[swiperIndex] && (
        // 카드 재발급 해지 안내
        <Modal isOpen={isTerminateNoticeForReissueModal} contentLabel="modal" onRequestClose={() => {
          setTerminateNoticeForReissueModal(false);
        }} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
          <p className="message">{`${t("SERV06.popup06", "Please apply for a new card after terminating your existing card.")}`}</p>
          <div className="btnWrapTy02">
            <button type="button" className="btnPrimaryTy01" onClick={() => {
              setTerminateNoticeForReissueModal(false);
            }}><span>{t("COMMON.button.confirm")}</span></button>
          </div>
        </Modal>
      )}
      <Modal
        isOpen={isModalView}
        onRequestClose={closeModal}
        className="popTyLayer2"
        overlayClassName="dim"
        closeTimeoutMS={500}>
        <div>
          <div className="Please-pay-Pending-Fee">
            <span>
              {t('APPL02.pendingFee').replace(
                '{0}',
                StringUtils.nvlDefault(
                  StringUtils.moneyFormat(totalPeningFeeAmount)
                )
              )}
            </span>
          </div>
        </div>
        <div className="btnWrapTy02">
          <button
            type="button"
            className="btnPrimaryTy01"
            onClick={() => clickMoveToAppWeb('N039')}>
            <span>{t('SERV16.button.pay')}</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MainPage;